import { useState } from 'react';

import { GetDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import styles from './SoleTrader.module.css';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner/LoadingSpinner';
import { useStateKey } from '../../../../../hooks/useStateKey.hook';
import { DocumentUpload } from '../../DocumentUpload/DocumentUpload';
import {
  checkCorrectNumberOfFiles,
  checkCorrectFileType,
} from '../../DocumentUpload/validateFiles';
import { ResolutionMethods } from '../../getBankDetailRequirements';
import {
  BankAccountSortCodeInputs,
  validateBankAccountNumber,
  validateSortCode,
} from '../components/BankAccountSortCodeInputs/BankAccountSortCodeInputs';
import { FormHead } from '../components/FormHead/FormHead';
import { OnfidoButton } from '../components/OnfidoButton/OnfidoButton';
import { OnfidoStatus } from '../components/OnfidoButton/useOnfido.hook';
import { createPaymentMethod } from '../createPaymentMethod';
import formStyles from '../SecureYourPaymentsForm.module.css';
import { useBankDetails } from '../useBankDetails.hook';
import { useUploadBankDocuments } from '../useUploadBankDocuments';

type SoleTraderFormValues = {
  bankAccountNumber: string;
  sortCode: string;
  files: File[];
  onfidoPhoneNumber: string;
  onfidoStatus: OnfidoStatus;
};

export const SoleTrader = ({
  hasUploadedFiles,
  fetchRequirements,
  uploadedFiles,
  getUploadedDocuments,
  resolutionMethods,
  isLoading = false,
}: {
  hasUploadedFiles: boolean;
  fetchRequirements: () => void;
  uploadedFiles: GetDocumentUploadResponse['data'];
  getUploadedDocuments: () => void;
  resolutionMethods?: ResolutionMethods;
  isLoading?: boolean;
}) => {
  const { stateKey } = useStateKey();
  const [error, setError] = useState(false);
  const { bankAccountNumber, sortCode } = useBankDetails();
  const { uploadDocuments, isLoadingUploadDocuments } =
    useUploadBankDocuments();
  const isUpdating = isLoading || isLoadingUploadDocuments;

  if (!stateKey) {
    return <></>;
  }

  const handleSubmit = async (values: SoleTraderFormValues) => {
    // Bank Wizard Checks
    try {
      await createPaymentMethod({
        accountNumber: values.bankAccountNumber,
        sortCode: values.sortCode,
        stateKey,
      });

      // Uploads Documents
      await uploadDocuments({ files: values.files });

      getUploadedDocuments();
      fetchRequirements();
    } catch (e) {
      setError(true);
    }
  };

  const validateFiles = () => {
    if (hasUploadedFiles) {
      return Yup.array().nullable();
    }

    return Yup.array()
      .required('FILE_UPLOAD_REQUIRED')
      .test(
        'is-correct-number-of-files',
        'VALIDATION_NUMBER_OF_FILES_UPLOADED',
        (files: unknown[] | null | undefined) =>
          checkCorrectNumberOfFiles(files, 1),
      )
      .test(
        'is-correct-file-type',
        'VALIDATION_CORRECT_FILE_TYPE',
        checkCorrectFileType,
      );
  };

  const validationSchema = Yup.object().shape({
    bankAccountNumber: validateBankAccountNumber(),
    sortCode: validateSortCode(),
    files: validateFiles(),
  });

  return (
    <Formik
      initialValues={{
        bankAccountNumber,
        sortCode,
        files: [],
        onfidoPhoneNumber: '',
        onfidoStatus: 'NOT_STARTED',
      }}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors }) => {
        const isDisabled = Object.keys(errors).length !== 0;
        const hasSubmitted =
          !!bankAccountNumber && !!sortCode && hasUploadedFiles;
        const isBankDetailsSubmitDisabled = isDisabled || hasSubmitted;

        return (
          <Form className={styles.form}>
            <BankAccountSortCodeInputs
              accountNumberName="bankAccountNumber"
              sortCodeName="sortCode"
            />
            <FormHead
              header="Upload a bank statement from the last 3 months"
              description="Upload or drag and drop a bank statement to secure the account."
            >
              <DocumentUpload
                filesName="files"
                isPendingDocumentChecks={hasUploadedFiles}
                uploadedFiles={uploadedFiles}
              />
            </FormHead>

            <Button
              variant="primary"
              type="submit"
              className={styles.submitButton}
              disabled={isBankDetailsSubmitDisabled}
            >
              {isUpdating ? (
                <LoadingSpinner />
              ) : hasSubmitted ? (
                'Saved'
              ) : (
                'Submit'
              )}
            </Button>

            {resolutionMethods?.documentUpload?.errors.map((error) => (
              <div key={error} className={styles.errorTextContainer}>
                {error}
              </div>
            ))}

            {error && (
              <div className={styles.errorTextContainer}>
                We’re not able to save your details, please call 0203 778 054
              </div>
            )}

            <FormHead
              header="Verify your identity"
              description="It takes 5 minutes do to on your phone with Onfido- just send yourself a text to get started and have some ID to hand."
              className={formStyles.formSection}
            >
              <OnfidoButton
                onfidoPhoneNumberName="onfidoPhoneNumber"
                onfidoStatusName="onfidoStatus"
                className={formStyles.formInput}
              />
            </FormHead>
          </Form>
        );
      }}
    </Formik>
  );
};
